import _ from "lodash";
import { faListTree, faMessageCaptions, faSquareCheck } from "@fortawesome/pro-regular-svg-icons";
import { getDefaultLanguage } from "./appCore";
import { RETURN_REASON_TYPES as RETURN_REASON_TYPES_FROM_RYLAK } from "rylak-common/src/constants";

export const DEFAULT_RETURN_REASON_NAME = "Default";

// TODO Just use the ones in Rylak
export const RETURN_REASON_TYPES = RETURN_REASON_TYPES_FROM_RYLAK;

export const RETURN_REASON_TYPE_DATA = {
    [RETURN_REASON_TYPES.SIMPLE]: {
        icon: faSquareCheck,
        tooltip: "Simple checkbox",
    },
    [RETURN_REASON_TYPES.FREE_TEXT]: {
        icon: faMessageCaptions,
        tooltip: "Free text",
    },
    [RETURN_REASON_TYPES.MULTIPLE_CHOICE]: {
        icon: faListTree,
        tooltip: "Multiple choice",
    },
    [RETURN_REASON_TYPES.CHECKBOX]: {
        icon: faSquareCheck,
        iconMultiple: faListTree,
        tooltip: "Checkbox",
    },
};

export function getReturnReasonSetupId(returnReasonSetup) {
    return returnReasonSetup?.id;
}

export function getProductCategories(returnReasonSetup) {
    return returnReasonSetup?.productCategories;
}

export function getProductCategoryIds(returnReasonSetup) {
    return getProductCategories(returnReasonSetup)?.map((p) => p.id);
}

export function getProductCategoryFromId(id, productCategories) {
    return productCategories.find((p) => p.id === id);
}

export function getReturnReasons({ returnReasonSetup }) {
    return returnReasonSetup?.returnReasons;
}

export function getReturnReasonSetupName(returnReasonSetup) {
    return returnReasonSetup?.name;
}

export function getReturnReasonTranslations({ returnReason }) {
    return returnReason?.translations;
}

export function getSubReasons({ returnReason }) {
    return returnReason?.subReasons;
}

export function isSubReason({ returnReason }) {
    return !getReturnReasonType({ returnReason });
}

export function getReturnReasonType({ returnReason }) {
    return returnReason?.type;
}

export function getReturnReasonExternalCode({ returnReason }) {
    return returnReason?.externalCode;
}

export function getReturnReasonIcon({ returnReason }) {
    const returnReasonData = getReturnReasonData({ returnReason });
    if (getSubReasons({ returnReason })?.length) {
        return returnReasonData?.iconMultiple || returnReasonData?.icon;
    }
    return returnReasonData?.icon;
}

export function getReturnReasonTooltip({ returnReason }) {
    return getReturnReasonData({ returnReason })?.tooltip;
}

export function getReturnReasonData({ returnReason }) {
    return RETURN_REASON_TYPE_DATA[getReturnReasonType({ returnReason })];
}

export function isSubReasonHidden({ returnReason }) {
    return !!returnReason?.hidden;
}

export function isReturnReasonMultipleChoice({ returnReason }) {
    return getReturnReasonType({ returnReason }) === RETURN_REASON_TYPES.MULTIPLE_CHOICE && getSubReasons({ returnReason })?.length; // We have multiple_choice type but better to look for if subReasons exists for backwards compability
}

export function canAddSubReturnReasons({ returnReason }) {
    // Probably want to remove this when combining return and complaint reasons
    const returnReasonType = getReturnReasonType({ returnReason });
    return returnReasonType === RETURN_REASON_TYPES.CHECKBOX || returnReasonType === RETURN_REASON_TYPES.MULTIPLE_CHOICE;
}

export function getReturnReasonTranslated({ returnReason, selectedLanguage }) {
    return getReturnReasonTranslations({ returnReason })?.[selectedLanguage];
}

export function getReturnReasonTranslatedSmart({ returnReason, selectedLanguage, appProps }) {
    let result = getReturnReasonTranslated({ returnReason, selectedLanguage });
    if (result) {
        return result;
    }
    const tryMainLanguage = getDefaultLanguage(appProps);
    result = getReturnReasonTranslated({
        returnReason,
        selectedLanguage: tryMainLanguage,
    });
    if (result) {
        return result;
    }
    const translations = getReturnReasonTranslations({ returnReason }) || {};
    const finalValue = Object.values(translations).find((v) => v);
    if (!finalValue) {
        return returnReason?.label || returnReason?.name || returnReason?.id;
    }
    return finalValue;
}

export function getReturnReasonMissingTranslations({ returnReason, enabledLanguages }) {
    const translations = getReturnReasonTranslations({ returnReason });
    return enabledLanguages.reduce((missingLanguages, language) => {
        if (!translations?.[language]) {
            missingLanguages = missingLanguages.concat(language);
        }
        return missingLanguages;
    }, []);
}

export function getReturnReasonAndSubReasonMissingTranslations({ returnReason, enabledLanguages }) {
    const missingLanguages = getReturnReasonMissingTranslations({
        returnReason,
        enabledLanguages,
    });
    return _.uniq(
        missingLanguages.concat(
            getSubReasonsMissingTranslations({
                returnReason,
                enabledLanguages,
            }),
        ),
    );
}

export function getSubReasonsMissingTranslations({ returnReason, enabledLanguages }) {
    const subReasons = getSubReasons({ returnReason });
    if (subReasons) {
        return _.uniq(
            subReasons.reduce((result, subReason) => {
                return result.concat(
                    getReturnReasonMissingTranslations({
                        returnReason: subReason,
                        enabledLanguages,
                    }),
                );
            }, []),
        );
    }
    return [];
}

export function getReturnReasonSetupMissingTranslations({ returnReasonSetup, enabledLanguages }) {
    return getReturnReasons({ returnReasonSetup }).reduce((result, returnReason) => {
        return _.uniq(
            result.concat(
                getReturnReasonAndSubReasonMissingTranslations({
                    returnReason,
                    enabledLanguages,
                }),
            ),
        );
    }, []);
}

export function getReturnReasonId({ returnReason }) {
    return returnReason?.id || returnReason?.createdId;
}

export function getReturnReasonIndex({ returnReasons, id }) {
    return returnReasons?.findIndex((r) => getReturnReasonId({ returnReason: r }) === id);
}

export function getReturnReason({ returnReasons, id }) {
    return returnReasons?.[getReturnReasonIndex({ returnReasons, id })];
}

export function getComplaintSubReason(complaintReason, { complaintSubReasonId }) {
    return getSubReasons({ returnReason: complaintReason })?.find((complaintSubReason) => complaintSubReason?.id === complaintSubReasonId);
}
